import React, { useMemo, useState } from 'react';
import { FaTimes, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/InventoryProducts.css';
import '../styles/InventoryProcurement.css';

const BULK = `
  mutation CreateSuppliersBulk($inputs: [CreateSupplierInput!]!) {
    createSuppliersBulk(inputs: $inputs) {
      created
      skipped
      skippedNames
    }
  }
`;

function emptyRow() {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    paymentTerms: '',
  };
}

export default function BulkAddSuppliersModal({ onClose, onSaved }) {
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [saving, setSaving] = useState(false);

  const filledCount = useMemo(
    () => rows.filter((r) => String(r.name || '').trim()).length,
    [rows],
  );

  const updateRow = (key, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const inputs = rows
      .map((r) => ({
        name: String(r.name || '').trim(),
        contactPerson: String(r.contactPerson || '').trim() || undefined,
        phone: String(r.phone || '').trim() || undefined,
        email: String(r.email || '').trim() || undefined,
        paymentTerms: String(r.paymentTerms || '').trim() || undefined,
      }))
      .filter((r) => r.name);
    if (!inputs.length) {
      toast.error('Add at least one supplier with a company name');
      return;
    }
    setSaving(true);
    try {
      const data = await graphqlRequest(BULK, { inputs });
      const res = data?.createSuppliersBulk;
      const created = Number(res?.created) || 0;
      const skipped = Number(res?.skipped) || 0;
      if (created > 0) {
        toast.success(
          `Added ${created} supplier${created === 1 ? '' : 's'}${
            skipped ? ` (${skipped} already existed)` : ''
          }`,
        );
        onSaved?.();
        if (!skipped) onClose?.();
      } else if (skipped) {
        toast.warn('Those suppliers already exist');
      } else {
        toast.error('No suppliers were added');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to add suppliers');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="iproc-overlay" onClick={onClose}>
      <div
        className="iproc-modal iproc-modal--wide"
        role="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="iproc-modal-header">
          <h2>Bulk Add Suppliers</h2>
          <button
            type="button"
            className="iproc-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>
        <form className="iproc-modal-body" onSubmit={handleSubmit}>
          <p className="ip-bulk-hint">
            Add several supplier companies at once. Product catalogs are not
            uploaded here — after saving, open a supplier and use
            <strong> Upload Word catalog</strong>, or add one supplier with
            their Word list from <strong>Add Supplier</strong>.
          </p>
          <div className="ip-bulk-table-wrap">
            <table className="ip-bulk-table">
              <thead>
                <tr>
                  <th>Company Name *</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Payment Terms</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <input
                        value={row.name}
                        onChange={(e) =>
                          updateRow(row.key, 'name', e.target.value)
                        }
                        placeholder="Supplier name"
                      />
                    </td>
                    <td>
                      <input
                        value={row.contactPerson}
                        onChange={(e) =>
                          updateRow(row.key, 'contactPerson', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        value={row.phone}
                        onChange={(e) =>
                          updateRow(row.key, 'phone', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="email"
                        value={row.email}
                        onChange={(e) =>
                          updateRow(row.key, 'email', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        value={row.paymentTerms}
                        onChange={(e) =>
                          updateRow(row.key, 'paymentTerms', e.target.value)
                        }
                        placeholder="Net 30"
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ip-bulk-remove"
                        onClick={() =>
                          setRows((prev) =>
                            prev.length <= 1
                              ? prev
                              : prev.filter((r) => r.key !== row.key),
                          )
                        }
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="ip-bulk-add-row"
            onClick={() => setRows((prev) => [...prev, emptyRow()])}
          >
            <FaPlus /> Add row
          </button>
          <div className="iproc-modal-footer">
            <span className="ip-bulk-count">
              {filledCount} supplier{filledCount === 1 ? '' : 's'} ready
            </span>
            <button type="button" className="iproc-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="iproc-btn iproc-btn--primary"
              disabled={saving}
            >
              {saving ? 'Adding…' : 'Add Suppliers'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
